import React, { createRef, forwardRef, useCallback, useEffect, useState } from "react";
import { CenterProps } from "@chakra-ui/react";
import heic2any from "heic2any";
import Dropzone, { Accept, DropzoneRef, FileError, FileRejection } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import {
  Col,
  Row,
  UiBox,
  UiCenter,
  UiHStack,
  UiIcon,
  UiImage,
  UiSimpleGrid,
  UiSpinner,
  UiText,
  UiVStack,
  useBreakpointValue,
} from "~/components/elements";
import { AppDispatch } from "~/redux/root-store";
import { colors } from "~/utils/theme";
import "react-lazy-load-image-component/src/effects/blur.css";
import { DUPLICATE_UPLOAD_MSG } from "~/constants/notification-messages";
import { removeSelectedPhoto, ReturnTypeUploadPhotoMisc, uploadPhotoMisc } from "~/redux/dashboards/thunk";
import { requestOK } from "redux-thunk-kit";
import { EUploadFileStatus } from "~/constants/dashboard";
import { getFileKey } from "~/utils/helpers/dashboard-dropzone";
import isEmpty from "lodash/isEmpty";
import { VerifyResultImage } from "~/types/models/dashboard";
import { unwrapResult } from "@reduxjs/toolkit";
import { AiFillCloseCircle } from "react-icons/ai";
import { RootState } from "~/redux/root-reducer";
import uniq from "lodash/uniq";
import { compact } from "lodash";

export interface UploadResponse extends VerifyResultImage {
  file: File | Blob;
}

type AcceptType = "VIDEO" | "PDF" | "IMAGE" | "AUDIO" | "ANDROID_IMAGE";

interface Props {
  placeholder?: string | null;
  placeholderComponent?: React.ReactNode;
  contentProps?: CenterProps;
  onSuccess?: (arg: VerifyResultImage["file"][]) => any;
  onRemove?: (arg: VerifyResultImage) => any;
  acceptType?: AcceptType[];
  multiple?: boolean;
  preview?: boolean;
  endpoint?: string;
  maxFiles?: number;
  defaultFiles?: any[];
  files?: any[];
  isDisabled?: boolean;
  type?: "first_person" | "second_person";
}

const getAcceptFileTypes = (types: AcceptType[]): Accept => {
  const map = {
    VIDEO: "video/*",
    AUDIO: "audio/*",
    IMAGE: "image/*",
    ANDROID_IMAGE: "application/octet-stream",
    PDF: "application/pdf",
  };
  const accept = {} as Accept;
  types.forEach(type => {
    if (map[type]) {
      accept[map[type]] = type === "IMAGE" ? [".heic", ".heif"] : [];
    }
  });
  return accept;
};

const dimensionValidator = (file: any): FileError | FileError[] | null => {
  if (file.width < minWidth || file.height < minHeight) {
    return {
      code: "name-too-large",
      message: `Photos must be at least ${minWidth}px x ${minHeight}px`,
    };
  }

  return null;
};

// This function is only support when drag and drop
const handleGetFileFromEvent = async (event: any): Promise<Array<File | DataTransferItem>> => {
  const files = event.target.files || event.dataTransfer.files;
  const promises = [];
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    // eslint-disable-next-line @typescript-eslint/no-loop-func
    const promise: Promise<File | DataTransferItem> = new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        file.width = image.width;
        file.height = image.height;
        file.localUrl = URL.createObjectURL(file);
        resolve(file);
      };
      image.src = URL.createObjectURL(file);
    });
    promises.push(promise);
  }
  return Promise.all(promises);
};

const getImagesDimensionProcess = (imgs: any): Promise<((File | Blob) & { width: number; height: number })[]> =>
  Promise.all(
    imgs.map(
      (img: any) =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.src = URL.createObjectURL(img);
          if (img.type === "image/heic" || img.type === "image/heif") {
            fetch(image.src)
              .then(res => res.blob())
              .then(blob => heic2any({ blob, toType: "image/jpeg" }))
              .then((conversionResult: any) => {
                const newImg = new Image();
                newImg.src = URL.createObjectURL(conversionResult);
                newImg.onload = () => {
                  conversionResult.width = newImg.width;
                  conversionResult.height = newImg.height;
                  // When converting from HEIC/HEIF to JPEG, the size differs each time, so we get the initial size before converting.
                  // Override Blob's size property
                  Object.defineProperty(conversionResult, "size", {
                    value: img.size,
                  });
                  resolve(conversionResult);
                };
              })
              .catch(error => {
                if (error?.code === 1) {
                  resolve(processOtherImageType(img));
                }
                reject(error);
              });
          } else {
            image.onload = () => {
              img.width = image.width;
              img.height = image.height;
              resolve(img);
            };
          }
        })
    )
  );

const getValidatedFiles = (files: ((File | Blob) & { width: number; height: number })[]) =>
  files.filter(file => file.width >= minWidth && file.height >= minHeight);

const checkDuplicateFiles = (oldFiles: VerifyResultImage[], newFiles: (File | Blob)[]) => {
  // Param 1: [A, B, C]
  // Param 2: [C, D, E]
  // Result: {[A, B, C, D, E], isDup}

  const oldFileObjects = oldFiles.map(obj => obj.file ?? obj);
  // Include uploaded images and new images
  const allFiles: (File | Blob | VerifyResultImage)[] = [...oldFileObjects, ...newFiles];

  const filteredFileObj = allFiles.reduce((accumulator, currentValue) => {
    const fileKey =
      currentValue instanceof File || currentValue instanceof Blob
        ? getFileKey(currentValue)
        : getFileKey(currentValue.meta || currentValue.localMeta);

    return {
      [fileKey]: currentValue,
      ...accumulator,
    };
  }, {} as { [key: string]: File | Blob | VerifyResultImage });

  const filteredFiles = Object.values(filteredFileObj);

  return { filteredFiles, isDuplicated: allFiles.length > filteredFiles.length };
};

/**
 * This function is used for getting newly uploaded images after removing duplicates
 * @param allFilteredFiles
 * @param uploadedFiles
 */
const getNewAcceptedFiles = (
  allFilteredFiles: (File | Blob | VerifyResultImage)[],
  uploadedFiles: VerifyResultImage[]
) => {
  // Param 1: [A, B, C, D, E]
  // Param 2: [A, B, C]
  // Result: [D, E]: {localUrl: string, file: File}[]
  if (allFilteredFiles.length === 0) {
    return [];
  }

  if (uploadedFiles.length === 0) {
    return allFilteredFiles.map(item => ({
      localUrl: URL.createObjectURL(item as File),
      file: item as File,
    }));
  }

  const uploadedFileKeys = uploadedFiles.map(item => getFileKey(item.file || item.meta || item.localMeta));
  return allFilteredFiles
    .filter(item => {
      // const isFileSelected = uploadedFiles.some(
      //   fileObj => getFileKey(item) === (fileObj.file ? getFileKey(fileObj.file) : getFileKey(fileObj))
      // );
      // return !isFileSelected;

      const filteredFileKey =
        item instanceof File || item instanceof Blob ? getFileKey(item) : getFileKey(item.meta || item.localMeta);

      return !uploadedFileKeys.includes(filteredFileKey);
    })
    .map(acctFile => ({
      localUrl: URL.createObjectURL(acctFile as File),
      file: acctFile as File,
    }));
};

const getUploadedErrorFiles = (data: ReturnTypeUploadPhotoMisc[]) =>
  data.filter(fileObj => {
    const { localMeta, localUrl, errMsg, ...rest } = fileObj.invalidImage || {};
    const isInvalidImage = !isEmpty(rest);

    return (
      fileObj.status === EUploadFileStatus.FAIL || fileObj.status === EUploadFileStatus.DUPLICATE || isInvalidImage
    );
  });

const getUploadedValidFiles = (data: ReturnTypeUploadPhotoMisc[]) =>
  data.filter(fileObj => {
    if (isEmpty(fileObj.validImage)) {
      return false;
    }
    const { errMsg } = fileObj.validImage;
    return !errMsg;
  });

const getErrorMessagesFromBE = (data: ReturnTypeUploadPhotoMisc[]) => data.map(i => i.errMsg);

const getAllUploadedFiles = (
  filteredFiles: (VerifyResultImage | File | Blob)[],
  errorFiles: ReturnTypeUploadPhotoMisc[],
  validFiles: ReturnTypeUploadPhotoMisc[],
  isVerifyStep?: boolean
) => {
  // Param 1: [A, B, C, D, E],
  // Param 2: [E],
  // Result: [A, B, C, D]

  const errorFileObjects = errorFiles.reduce((accumulator, currentValue) => {
    const { fileId } = currentValue;
    return {
      [fileId]: currentValue,
      ...accumulator,
    };
  }, {} as { [key: string]: ReturnTypeUploadPhotoMisc });

  const errorFileKeys = Object.keys(errorFileObjects);
  const validFileObjects = validFiles.reduce((accumulator, currentValue) => {
    const { fileId } = currentValue;
    return {
      [fileId]: currentValue,
      ...accumulator,
    };
  }, {} as { [key: string]: ReturnTypeUploadPhotoMisc });

  const validFileKeys = Object.keys(validFileObjects);

  const allUploadedFiles = filteredFiles.map(file => {
    const fileKey =
      file instanceof File || file instanceof Blob ? getFileKey(file) : getFileKey(file.meta || file.localMeta);

    if (
      errorFileKeys.includes(fileKey) &&
      (errorFileObjects[fileKey].status === EUploadFileStatus.FAIL ||
        errorFileObjects[fileKey].status === EUploadFileStatus.DUPLICATE ||
        isVerifyStep)
    ) {
      return null;
    }

    // For the old files that are fetched from server, they are not a File anymore
    // We need to check instance of Blob because of the HEIC format
    // TODO: Check Blob only => test
    if (!(file instanceof File) && !(file instanceof Blob)) {
      return file;
    }

    // @ts-ignore
    let entityId = file?.id;
    if (!entityId && !isEmpty(validFileObjects[fileKey])) {
      // @ts-ignore
      entityId = validFileObjects[fileKey]?.validImage?.id;
    }

    return {
      localUrl: URL.createObjectURL(file),
      file,
      id: entityId,
    };
  });

  return allUploadedFiles.filter(file => file);
};

const getNewUploadedAcceptedFiles = (
  files: { localUrl: string; file: File }[],
  errorFiles: ReturnTypeUploadPhotoMisc[],
  isVerifyStep?: boolean
) => {
  // Param 1: [D, E],
  // Param 2: [E],
  // Result: [D]

  const errorFileObjects = errorFiles.reduce((accumulator, currentValue) => {
    const { fileId } = currentValue;
    return {
      [fileId]: currentValue,
      ...accumulator,
    };
  }, {} as { [key: string]: ReturnTypeUploadPhotoMisc });

  const errorFileKeys = Object.keys(errorFileObjects);

  return files.filter(fileObj => {
    const fileKey = getFileKey(fileObj.file);

    if (
      errorFileKeys.includes(fileKey) &&
      (errorFileObjects[fileKey].status === EUploadFileStatus.FAIL ||
        errorFileObjects[fileKey].status === EUploadFileStatus.DUPLICATE ||
        isVerifyStep)
    ) {
      return false;
    }

    return true;
  });
};

/**
 * This function handles heic and heif images that have been converted from other formats, such as png.
 * @param img
 * @returns
 */
const processOtherImageType = (img: File): Promise<File> =>
  new Promise((resolve, reject) => {
    if (img.type === "image/heic" || img.type === "image/heif") {
      const image = new Image();
      image.src = URL.createObjectURL(img);
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.width || 0;
        canvas.height = image.height || 0;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(image, 0, 0, image.width || 0, image.height || 0);

          canvas.toBlob(blob => {
            if (blob) {
              const convertedImage = new File([blob], img.name, { type: "image/jpeg" });
              // @ts-ignore
              convertedImage.width = image.width || 0;
              // @ts-ignore
              convertedImage.height = image.height || 0;
              resolve(convertedImage);
            } else {
              reject(new Error("Conversion to JPEG failed"));
            }
          }, "image/jpeg");
        } else {
          reject(new Error("Canvas context is not supported"));
        }
      };
    } else {
      const image = new Image();
      image.src = URL.createObjectURL(img);
      image.onload = () => {
        // @ts-ignore
        img.width = image.width || 0;
        // @ts-ignore
        img.height = image.height || 0;
        resolve(img);
      };
    }
  });

const maxSize = 14680064; // 1048576 Bytes * 14 = 14MB
const minWidth = 100;
const minHeight = 100;

function PreviewImage({
  file,
  onClickRemove,
  showLoading = false,
}: {
  file: VerifyResultImage;
  onClickRemove?: (e: any, file: VerifyResultImage) => Promise<void>;
  showLoading?: boolean;
}) {
  const [loading, setLoading] = useState(false);

  const onClickRemoveLocal = async (e: any) => {
    setLoading(true);
    await onClickRemove?.(e, file);
    setLoading(false);
  };

  return (
    <UiBox pos="relative">
      {(showLoading || loading) && (
        <UiCenter pos="absolute" top={0} right={0} bottom={0} left={0} bg="white" opacity={0.3}>
          <UiSpinner color="neutral.gray300" />
        </UiCenter>
      )}
      <UiBox
        className="dashboard__dropzone-verify"
        maxWidth={{ base: "6.25rem", sm: "5.25rem" }}
        // maxHeight={{ base: undefined, sm: "5.25rem" }}
        aspectRatio={1}
        overflow="hidden"
        borderRadius="0.5rem"
      >
        <UiImage
          fallbackSrc={file.url}
          isLazy={false}
          alt=""
          width="100%"
          height="100%"
          src={file.localUrl || file.url}
          style={{ objectFit: "cover", height: "100%" }}
        />
      </UiBox>
      {!showLoading && !loading && onClickRemove && (
        <Col
          justify="center"
          align="center"
          pos="absolute"
          right={1}
          top={1}
          zIndex={1}
          bg="neutral.gray00"
          rounded="full"
          w={4}
          h={4}
          _hover={{ cursor: "pointer" }}
          onClick={onClickRemoveLocal}
        >
          <UiIcon as={AiFillCloseCircle} mx={0} boxSize={5} color="neutral.gray900" />
        </Col>
      )}
    </UiBox>
  );
}

export const DashboardDropzone = forwardRef(
  (
    {
      onSuccess,
      onRemove,
      preview = true,
      multiple = false,
      acceptType = ["PDF", "IMAGE", "VIDEO", "ANDROID_IMAGE"],
      placeholder = "Upload photos",
      placeholderComponent,
      contentProps = {},
      endpoint,
      maxFiles = 0,
      defaultFiles,
      files,
      isDisabled,
      type = "first_person",
    }: Props,
    ref: any
  ) => {
    const dispatch: AppDispatch = useDispatch();
    const vrTitle = useBreakpointValue<any>({ base: "title1", md: "title1" });
    const vrCaption = useBreakpointValue<any>({ base: "body2", md: "body3" });
    const { invalid_images: invalidImages } = useSelector((state: RootState) => state.dashboard.verifyResult) || {};
    const [selectedFiles, setSelectedFiles] = useState<VerifyResultImage[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<VerifyResultImage[]>([]);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
    const [validationFE, setValidationFE] = useState<string[]>();
    const dropzoneRef = createRef<DropzoneRef>();
    const Container = useBreakpointValue<any>({ base: UiSimpleGrid, sm: UiHStack });

    // useImperativeHandle(ref, () => ({
    //   openDialog() {
    //     // Note that the ref is set async,
    //     // so it might be null at some point
    //     if (dropzoneRef.current) {
    //       dropzoneRef.current.open();
    //     }
    //   },
    // }));

    useEffect(() => {
      if (defaultFiles && defaultFiles.length) {
        setSelectedFiles(defaultFiles);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (files && files.length) {
        setSelectedFiles(files);
      } else {
        setSelectedFiles([]);
      }
    }, [files]);

    useEffect(() => {
      if (invalidImages && invalidImages.length) {
        setErrorMessages(compact(uniq(invalidImages?.map(i => i.errMsg))));
      } else {
        setErrorMessages([]);
      }
    }, [invalidImages, setErrorMessages]);

    const onDrop = useCallback(
      async (acceptedFiles: File[]) => {
        if (!acceptedFiles || acceptedFiles.length === 0 || selectedFiles.length === maxFiles) {
          return;
        }
        const invalidFEMsgs: string[] = [];
        setValidationFE(invalidFEMsgs);

        if (selectedFiles.length + acceptedFiles.length > maxFiles) {
          setValidationFE([`You can only upload ${maxFiles} images`]);
          return;
        }

        try {
          setIsUploadingPhotos(true);
          const isVerifyStep = files && files.length >= 0;
          const filesWithDimension = await getImagesDimensionProcess(acceptedFiles);
          const validatedFiles = getValidatedFiles(filesWithDimension);
          const { filteredFiles, isDuplicated } = checkDuplicateFiles(selectedFiles, validatedFiles);
          const newAcceptedFiles = getNewAcceptedFiles(filteredFiles, selectedFiles);
          if (validatedFiles.length < acceptedFiles.length) {
            invalidFEMsgs.push(`Photo must be at least ${minWidth}px x ${minHeight}px`);
          }
          if (isDuplicated) {
            invalidFEMsgs.push(DUPLICATE_UPLOAD_MSG);
          }
          if (invalidFEMsgs.length > 0) {
            setValidationFE(compact(uniq(invalidFEMsgs)));
            return;
          }
          // @ts-ignore
          setUploadingFiles(newAcceptedFiles);
          const resAction = await dispatch(uploadPhotoMisc({ images: newAcceptedFiles, type }));
          setUploadingFiles([]);
          if (requestOK(resAction, uploadPhotoMisc)) {
            const result = unwrapResult(resAction);
            const errorFiles = getUploadedErrorFiles(result);
            const errFileStatusMsgs: string[] = [];
            if (errorFiles) {
              errorFiles.forEach(ef => {
                if (ef.status === EUploadFileStatus.FAIL || ef.status === EUploadFileStatus.DUPLICATE) {
                  errFileStatusMsgs.push(ef.status);
                }
              });
            }
            const validFiles = getUploadedValidFiles(result);
            const errMsgs = getErrorMessagesFromBE(result);
            setValidationFE(prev => compact(uniq([...(prev || []), ...(errMsgs || ""), ...(errFileStatusMsgs || "")])));
            const allUploadedFiles: any[] = getAllUploadedFiles(filteredFiles, errorFiles, validFiles, isVerifyStep);
            const newUploadedAcceptedFiles = getNewUploadedAcceptedFiles(newAcceptedFiles, errorFiles, isVerifyStep);

            setSelectedFiles(allUploadedFiles);
            // @ts-ignore
            onSuccess?.(newUploadedAcceptedFiles);
          }
        } catch (error: any) {
          setValidationFE(prev => compact(uniq([...(prev || []), error?.message || ""])));
        } finally {
          setIsUploadingPhotos(false);
        }
      },
      [selectedFiles, maxFiles, dispatch, onSuccess, files, type]
    );

    const onDropRejected = (errors: FileRejection[]) => {
      if (errors.length) {
        if (errors[0].errors?.[0].code === "file-too-large") {
          setValidationFE(prev => compact(uniq([...(prev || []), "File is larger than 14MB"])));
        } else {
          setValidationFE(prev => compact(uniq([...(prev || []), errors[0].errors?.[0].message])));
        }
      }
    };

    const onRemovePreview = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>, f: VerifyResultImage) => {
      e.stopPropagation();
      const newFiles = selectedFiles.filter(file => {
        const found = (file.localUrl && file.localUrl === f.localUrl) || (file.id && file.id === f.id);
        return !found;
      });

      if (f.id) {
        await dispatch(removeSelectedPhoto({ imageId: f.id }));
      }

      setSelectedFiles(newFiles);
      onRemove?.(f);
    };

    const renderPreview = () => {
      if (!preview || !selectedFiles || selectedFiles.length === 0) {
        return null;
      }

      return (
        <Col mt={4}>
          <Container
            pb={1}
            spacing={{ base: 2, lg: 3 }}
            wrap="wrap"
            columns={3}
            // overflowX="auto"
            // sx={{
            //   "&::-webkit-scrollbar": {
            //     height: "2px",
            //   },
            //   "&::-webkit-scrollbar-thumb": {
            //     backgroundColor: "blue",
            //     borderRadius: "20px",
            //   },
            // }}
          >
            {selectedFiles?.map((f, i) => (
              <PreviewImage
                key={`image-index-${f.localUrl || f.url}-${i}`}
                file={f}
                onClickRemove={async e => onRemovePreview(e, f)}
              />
            ))}
            {uploadingFiles?.map((f, i) => (
              <PreviewImage key={`image-index-${f.localUrl || f.url}-${i}`} file={f} showLoading={isUploadingPhotos} />
            ))}
          </Container>
          {/* <UiPrimaryButton */}
          {/*  mt={4} */}
          {/*  mx="auto" */}
          {/*  w={{ base: "full", md: "fit-content" }} */}
          {/*  variant="outline" */}
          {/*  borderColor="neutral.gray300" */}
          {/*  h="2.5rem" */}
          {/*  // borderColor="navy" */}
          {/*  bg="transparent" */}
          {/*  color="neutral.gray300" */}
          {/*  _hover={{ */}
          {/*    bg: "transparent", */}
          {/*    color: "neutral.gray300", */}
          {/*  }} */}
          {/*  onClick={() => { */}
          {/*    console.log(123); */}
          {/*  }} */}
          {/* > */}
          {/*  Check quality */}
          {/* </UiPrimaryButton> */}
        </Col>
      );
    };

    const renderPlaceholder = () => {
      if (placeholder === null) {
        return null;
      }

      if (placeholderComponent) {
        return (
          <Col flex="1" justify="center" align="center">
            {placeholderComponent}
          </Col>
        );
      }

      return (
        <Col flex="1" justify="center">
          <UiText textAlign="center" vr="body2" color="#267A9F">
            {placeholder}
          </UiText>
        </Col>
      );
    };

    return (
      <Col h="full" mt={4}>
        <Dropzone
          ref={dropzoneRef}
          onDrop={onDrop}
          accept={getAcceptFileTypes(acceptType)}
          maxFiles={maxFiles}
          onDropRejected={onDropRejected}
          maxSize={maxSize}
          noClick={selectedFiles.length === maxFiles}
          disabled={isDisabled}
          // validator={dimensionValidator}
          // getFilesFromEvent={handleGetFileFromEvent}
        >
          {({ getRootProps, getInputProps }) => (
            <Col flex={1} {...getRootProps()}>
              <Col
                pos="relative"
                bg={colors.alabaster}
                borderRadius="5px"
                minH="100px"
                _hover={{ cursor: "pointer" }}
                {...contentProps}
              >
                {isUploadingPhotos && (
                  <UiCenter pos="absolute" top={0} right={0} bottom={0} left={0} bg="white" opacity={0.8}>
                    <UiSpinner color="neutral.gray300" />
                  </UiCenter>
                )}
                {renderPlaceholder()}
                <input {...getInputProps()} />
              </Col>
            </Col>
          )}
        </Dropzone>
        {/* <UiVStack gap={1} alignItems="flex-start" mt={2}> */}
        {/*  {!!validationFE?.length && */}
        {/*    validationFE.map((e, idx) => ( */}
        {/*      <UiText key={`${e}-${idx}`} color="red" vr="body2"> */}
        {/*        {`*${e}`} */}
        {/*      </UiText> */}
        {/*    ))} */}
        {/* </UiVStack> */}

        {renderPreview()}

        {(!!errorMessages?.length || !!validationFE?.length) && (
          <Col rounded="2xl" bg="#FFF1EB" p={{ base: 4, lg: 5 }} mt={4}>
            <Row>
              <UiIcon as={AiFillCloseCircle} mx={0} boxSize={6} color="red" />
              <Col ml={2}>
                <UiText vr={vrTitle}>Declined Photos</UiText>
                <UiVStack gap={1} alignItems="flex-start" mt={0} pt={1}>
                  {errorMessages.map((ii, idx) => (
                    <UiText key={`${ii}-${idx}`} vr={vrCaption}>
                      {`${ii}`}
                    </UiText>
                  ))}
                  {!!validationFE?.length &&
                    validationFE.map((e, idx) => (
                      <UiText key={`${e}-${idx}`} vr={vrCaption}>
                        {`${e}`}
                      </UiText>
                    ))}
                </UiVStack>
              </Col>
            </Row>
            <UiHStack
              mt={4}
              columnGap={{ base: 2, lg: 3 }}
              overflowX="auto"
              sx={{
                "&::-webkit-scrollbar": {
                  height: "2px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "blue",
                  borderRadius: "20px",
                },
              }}
            >
              {invalidImages?.map((f: VerifyResultImage, i) => (
                <PreviewImage
                  key={`image-index-${f.localUrl || f.url}-${i}`}
                  file={f}
                  onClickRemove={e => onRemovePreview(e, f)}
                />
              ))}
            </UiHStack>
          </Col>
        )}
      </Col>
    );
  }
);
